import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, message } from 'antd'
import { loadStripe } from '@stripe/stripe-js'
import MatImage from '../../../../AeroComponents/Image'
import { IMAGES_SRC } from '../../../../AeroComponents/Image/image.constants'
import { post } from '../../../../Api/http-interceptor'
import { urlConfig } from '../../../../Services/UtilityService'

const IdTab = props => {
	const { profileData, refreshProfile } = props
	const [verificationState, setVerificationState] = useState('IDLE')
	const [isStartingVerification, setIsStartingVerification] = useState(false)
	const pollingRef = useRef(null)

	const stripePromise = useMemo(() => {
		const countryId = urlConfig()?.countryId
		const publishableKey = countryId === 1 ? process.env.REACT_APP_AU_STRIPE_CLIENT_KEY : process.env.REACT_APP_NZ_STRIPE_CLIENT_KEY

		if (!publishableKey) {
			return null
		}

		return loadStripe(publishableKey)
	}, [])

	const clearPolling = () => {
		if (pollingRef.current) {
			window.clearInterval(pollingRef.current)
			pollingRef.current = null
		}
	}

	const startPollingForVerification = () => {
		clearPolling()
		let retries = 0
		pollingRef.current = window.setInterval(async () => {
			retries += 1
			if (refreshProfile) {
				await refreshProfile()
			}
			if (retries >= 12) {
				clearPolling()
			}
		}, 10000)
	}

	useEffect(() => {
		if (profileData && profileData.verified) {
			setVerificationState('VERIFIED')
			clearPolling()
		} else if (profileData && profileData.identityVerificationStatus === 'PENDING_VERIFICATION') {
			setVerificationState('PENDING')
		} else if (profileData && profileData.identityVerificationStatus === 'VERIFICATION_FAILED') {
			setVerificationState('FAILED')
		}
	}, [profileData])

	useEffect(() => {
		return () => {
			clearPolling()
		}
	}, [])

	const startVerification = async () => {
		if (!stripePromise) {
			message.error('Stripe is not configured. Please try again later.')
			return
		}

		setIsStartingVerification(true)
		setVerificationState('STARTING')

		try {
			const sessionResponse = await post('CREATE_IDENTITY_VERIFICATION_SESSION')
			const clientSecret = sessionResponse?.data?.clientSecret

			if (!clientSecret) {
				throw new Error('Unable to create verification session.')
			}

			const stripe = await stripePromise
			if (!stripe) {
				throw new Error('Stripe failed to load.')
			}

			const result = await stripe.verifyIdentity(clientSecret)
			const verificationError = result?.error

			if (!verificationError) {
				setVerificationState('PENDING')
				message.success('Verification submitted, pending review.')
				if (refreshProfile) {
					await refreshProfile()
				}
				startPollingForVerification()
				return
			}

			const errorType = verificationError.type || verificationError.code || ''
			if (errorType.includes('verification_canceled') || errorType.includes('verification_cancelled')) {
				setVerificationState('CANCELED')
				message.info('Identity verification was canceled. You can retry anytime.')
			} else {
				setVerificationState('FAILED')
				message.error(verificationError.message || 'Identity verification failed. Please try again.')
			}
		} catch (error) {
			setVerificationState('FAILED')
			message.error(error?.status_message || error?.message || 'Unable to start identity verification. Please try again.')
		} finally {
			setIsStartingVerification(false)
		}
	}

	if (profileData && profileData.verified) {
		return (
			<>
				<div style={{ fontSize: '16px', color: '#47464A' }}>ID Details</div>
				<div style={{ color: '#84818A', marginTop: '10px' }}>Identity verification helps secure the platform for pilots and customers.</div>
				<div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', color: '#1D9A41', fontWeight: 600 }}>
					<MatImage src={IMAGES_SRC.CHECK_ICON} />
					<span style={{ marginLeft: '8px' }}>Identity Verified ✓</span>
				</div>
			</>
		)
	}

	return (
		<>
			<div style={{ fontSize: '16px', color: '#47464A' }}>ID Details</div>
			<div style={{ color: '#84818A', marginTop: '10px' }}>Verify your identity securely with Stripe Identity.</div>
			<div style={{ marginTop: '16px', marginBottom: '16px', color: '#47464A' }}>
				{verificationState === 'PENDING'
					? 'Verification pending review. This can take a little time.'
					: verificationState === 'CANCELED'
					? 'Verification canceled. Please retry.'
					: verificationState === 'FAILED'
					? 'Verification failed. Please contact support.'
					: 'Complete identity verification to confirm your account and access all features.'}
			</div>
			{verificationState !== 'PENDING' && verificationState !== 'STARTING' && verificationState !== 'FAILED' && (
				<Button type='primary' onClick={startVerification} loading={isStartingVerification}>
					Start Verification
				</Button>
			)}
		</>
	)
}

export default IdTab
